import UIkit from "uikit";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import SettingsService from "../../services/settings.service";
import GuideConfirmationDialog from "./confirmation-dialog";

const PUBLISH_DIALOG_ID = "guide-publish-confirmation";
const STABLE_SCROLLBAR_CLASS = "guide-designer-stable-scrollbar";

const formatDate = (value) =>
  value ? new Date(value).toLocaleString() : "Unknown date";

const revisionChanges = (revision) =>
  Array.isArray(revision.changes) && revision.changes.length
    ? revision.changes
    : [{ label: "Change details unavailable" }];

const revisionState = (revision) => {
  if (revision.restored_from_revision_id) {
    return "Reverted";
  }
  if (revision.state === "published") {
    return "Published";
  }
  return "Draft";
};

const RevisionControls = ({
  onBeforeSave,
  onPublished,
  disabled = false,
  publishLabel = "Publish",
  publishTitle = "Publish Guide",
  publishMessage = "Save the current layout and publish the guide to every guest?",
  revisionLabel = "Published from designer",
}) => {
  const [revisions, setRevisions] = useState([]);
  const [historyError, setHistoryError] = useState("");
  const [busy, setBusy] = useState(false);
  const historyRef = useRef(null);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await SettingsService.getGuestbookRevisions();
      setRevisions(Array.isArray(data) ? data : []);
      setHistoryError("");
    } catch {
      setHistoryError("Unable to load guide history.");
    }
  }, []);

  useEffect(() => {
    loadHistory().catch(() => undefined);
  }, [loadHistory]);

  useEffect(() => {
    document.documentElement.classList.add(STABLE_SCROLLBAR_CLASS);
    return () => {
      document.documentElement.classList.remove(STABLE_SCROLLBAR_CLASS);
    };
  }, []);

  const publish = async () => {
    setBusy(true);
    try {
      if (onBeforeSave) await onBeforeSave();
      await SettingsService.saveGuestbookRevision(
        "published",
        revisionLabel,
      );
      await loadHistory();
      if (onPublished) await onPublished();
      UIkit.modal(`#${PUBLISH_DIALOG_ID}`).hide();
      UIkit.notification(`${publishTitle.replace(/^Publish /, "")} published`, "success");
    } catch {
      UIkit.notification("Unable to save guide changes", "error");
    } finally {
      setBusy(false);
    }
  };

  const revert = async (revision) => {
    if (
      !window.confirm(
        `Revert the guide to ${formatDate(revision.created_at)}? The current guide will be backed up and this revert will be added to history.`,
      )
    ) {
      return;
    }

    setBusy(true);
    try {
      if (onBeforeSave) await onBeforeSave();
      await SettingsService.restoreGuestbookRevision(revision.id);
      UIkit.notification("Guide reverted and added to history", "success");
      window.location.reload();
    } catch {
      UIkit.notification("Unable to revert guide revision", "error");
      setBusy(false);
    }
  };

  const openHistory = () => {
    UIkit.offcanvas(historyRef.current, {
      flip: true,
      overlay: true,
      container: true,
    }).show();
  };

  return (
    <div className="guide-revision-controls">
      <div className="guide-revision-actions">
        <button
          type="button"
          className="uk-button hms-btn hms-white-btn guide-revision-history-button"
          disabled={busy || disabled}
          onClick={openHistory}
          aria-haspopup="dialog"
          aria-controls="guide-revision-history"
          aria-label="History"
          title="History"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v5l3 2" />
          </svg>
        </button>
        <button
          type="button"
          className="uk-button hms-btn hms-red-btn"
          disabled={busy || disabled}
          uk-toggle={`target: #${PUBLISH_DIALOG_ID}`}
        >
          {publishLabel}
        </button>
      </div>

      <GuideConfirmationDialog
        id={PUBLISH_DIALOG_ID}
        title={publishTitle}
        confirmLabel={busy ? "Publishing…" : publishTitle}
        confirmClassName="hms-red-btn"
        busy={busy}
        closeLabel="Close publish confirmation"
        onConfirm={publish}
      >
        <p>{publishMessage}</p>
      </GuideConfirmationDialog>

      {createPortal(
          <div
            id="guide-revision-history"
            ref={historyRef}
            className="uk-modal-full hms-modals guide-revision-history-offcanvas"
            uk-offcanvas="flip: true; overlay: true; container: true"
          >
            <div className="uk-offcanvas-bar">
              <div className="hms-modals-main">
              <button
                className="uk-offcanvas-close uk-close-large"
                type="button"
                uk-close="true"
                aria-label="Close guide history"
              />
              <div className="uk-modal-header">
                <h2 className="uk-modal-title">Guide History</h2>
              </div>
              <div className="uk-modal-body">
                <div className="uk-padding-small">
                  {historyError ? (
                    <div className="uk-alert-danger" uk-alert="true">
                      {historyError}
                    </div>
                  ) : revisions.length === 0 ? (
                    <p className="uk-text-muted uk-margin-remove">
                      No published revisions yet. Publish the guide to create
                      its first revision.
                    </p>
                  ) : (
                    <ul className="uk-list uk-list-large uk-margin-remove">
                    {revisions.map((revision) => {
                      const state = revisionState(revision);
                      const changes = revisionChanges(revision);

                      return (
                        <li key={revision.id}>
                          <div className="uk-card uk-card-default uk-card-small uk-background-default uk-border-rounded guide-revision-card">
                            <div className="uk-card-header">
                              <div className="uk-flex uk-flex-between uk-flex-top">
                                <div>
                                  <div className="uk-text-bold">
                                    {formatDate(revision.created_at)}
                                  </div>
                                  <div className="secondary uk-margin-small-top">
                                    By {revision.created_by_name || "Unknown user"}
                                  </div>
                                </div>
                                <span className="uk-text-meta">{state}</span>
                              </div>
                            </div>

                            <div className="uk-card-body">
                              <div className="uk-text-meta uk-text-uppercase">
                                {changes.length}{" "}
                                {changes.length === 1 ? "Change" : "Changes"}
                              </div>
                              <ul className="uk-list guide-revision-change-list">
                                {changes.map((change, index) => (
                                  <li
                                    key={`${revision.id}-${change.label}-${index}`}
                                    className="guide-revision-change"
                                  >
                                    <span
                                      className="guide-revision-change-bullet"
                                      aria-hidden="true"
                                    >
                                      •
                                    </span>
                                    <span className="guide-revision-change-text">
                                      {change.label}
                                      {change.before !== undefined &&
                                        change.after !== undefined && (
                                          <span className="guide-revision-change-value">
                                            {` — ${change.before} → ${change.after}`}
                                          </span>
                                        )}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </div>

                            <div className="uk-card-footer uk-flex uk-flex-right uk-flex-middle">
                              <button
                                type="button"
                                className="uk-button hms-btn hms-red-btn"
                                disabled={busy}
                                onClick={() => revert(revision)}
                              >
                                Revert
                              </button>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                    </ul>
                  )}
                </div>
              </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default RevisionControls;
